import { Head, Link } from "@inertiajs/react"
import { Calendar, Copy, Eye, FileText, Plus, Share2 } from "lucide-react"
import { useState } from "react"
import { format } from "date-fns"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { dashboardPath, newSharePath, sharePath, sharesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { toast } from "sonner"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: dashboardPath(),
  },
]

interface Share {
  id: number
  title: string
  description: string | null
  slug: string
  public_url: string
  view_count: number
  expires_at: string | null
  created_at: string
  file_count: number
}

interface DashboardProps {
  recent_shares: Share[]
}

export default function Dashboard({ recent_shares }: DashboardProps) {
  const [copiedId, setCopiedId] = useState<number | null>(null)

  const copyToClipboard = async (share: Share) => {
    try {
      await navigator.clipboard.writeText(share.public_url)
      setCopiedId(share.id)
      toast.success("Link copied to clipboard!")
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      toast.error("Failed to copy link")
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Dashboard" />
      <div className="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
        {/* Welcome Section */}
        <div className="mb-4">
          <h1 className="text-3xl font-bold tracking-tight">Welcome to ShareAnything</h1>
          <p className="text-muted-foreground mt-2">
            Upload files and create shareable links for easy distribution
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid gap-4 md:grid-cols-3 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Shares</CardTitle>
              <Share2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{recent_shares.length}</div>
              <p className="text-xs text-muted-foreground">
                Active shares in your account
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Views</CardTitle>
              <Eye className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {recent_shares.reduce((sum, share) => sum + share.view_count, 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                Views across all shares
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
              <Plus className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Link href={newSharePath()}>
                  <Button size="sm" className="w-full">
                    <Plus className="mr-2 h-4 w-4" />
                    New Share
                  </Button>
                </Link>
                <Link href={sharesPath()}>
                  <Button size="sm" variant="outline" className="w-full">
                    View All
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Shares */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Shares</CardTitle>
            <CardDescription>
              Your most recently created shares
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recent_shares.length === 0 ? (
              <div className="text-center py-8">
                <Share2 className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No shares yet</h3>
                <p className="text-muted-foreground mb-4">
                  Get started by creating your first share
                </p>
                <Link href={newSharePath()}>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Share
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {recent_shares.map((share) => (
                  <Card key={share.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex justify-between items-start">
                        <CardTitle className="text-base line-clamp-1">
                          {share.title}
                        </CardTitle>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(share)}
                          className="h-8 w-8 p-0"
                        >
                          <Copy className={`h-4 w-4 ${copiedId === share.id ? 'text-green-600' : ''}`} />
                        </Button>
                      </div>
                      {share.description && (
                        <CardDescription className="line-clamp-2 text-xs">
                          {share.description}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="pb-3">
                      <div className="space-y-2 text-xs text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <FileText className="h-3 w-3" />
                          <span>{share.file_count} {share.file_count === 1 ? 'file' : 'files'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Eye className="h-3 w-3" />
                          <span>{share.view_count} views</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>{format(new Date(share.created_at), 'MMM d, yyyy')}</span>
                        </div>
                        {share.expires_at && (
                          <div className="text-orange-600 dark:text-orange-400">
                            Expires {format(new Date(share.expires_at), 'MMM d, yyyy')}
                          </div>
                        )}
                      </div>
                      <div className="mt-3 flex gap-2">
                        <Link href={sharePath(share.id)} className="flex-1">
                          <Button size="sm" variant="outline" className="w-full">
                            View Details
                          </Button>
                        </Link>
                        <a 
                          href={share.public_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex-1"
                        >
                          <Button size="sm" className="w-full">
                            Open Share
                          </Button>
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}